/* eslint-disable arrow-parens */
import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import esriConfig from '@arcgis/core/config';
import type MapView from '@arcgis/core/views/MapView';
import TileInfo from '@arcgis/core/layers/support/TileInfo.js';
import SpatialReference from '@arcgis/core/geometry/SpatialReference.js';

import '@arcgis/core/assets/esri/themes/light/main.css';
import type { Extents } from '../../types/geo';
import { defaultExtents } from '../../types/geo';
import { useApp } from '../../hooks/use-app';
import mapLoadedEvent from '../../services/fetch/user-events/map-loaded-event';
import { SAFE_DEFAULT_BASEMAP } from '../../layer-picker/basemaps';
import arcGisFactory from '../../arcGIS/arcgis-factory';

import {
  MapPlacement,
  MIN_ZOOM,
} from './arcgis-types';
import type {
  MapComponent,
  MapPlugin,
} from './arcgis-types';

export const ARCGIS_MAP_VIEW_FEATURE_FLAG = 'new-arc-gis-map-view';
export const MANAGED_LAYER_PREFIX = 'fulcrum-managed';
export const FULCRUM_DATA_LAYER_ID = 'fulcrum-data-layer';
export const FULCRUM_DECK_LAYER_ID = 'fulcrum-deck-layer';
export const LASSO_DRAW_ACTIVE_CLASS = 'lasso-draw-active';

const StyledBox = styled(Box)({
  flexGrow: 1,
  width: '100%',
  height: '100%',
  minWidth: '100px',
  minHeight: '100px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  '&.google-tile': {
    '& .esri-attribution': {
      background: 'transparent',
      color: '#323232',
      fontSize: '11px',
      lineHeight: '21px',
      height: '21px',
      '& .esri-attribution__sources': {
        padding: '0',
        svg: {
          fill: '#474747',
          opacity: 0.8,
          marginLeft: '10px',
          marginBottom: '4px',
          g: {
            fill: '#ffffff',
            opacity: 1,
          },
        },
        span: {
          background: 'rgba(255, 255, 255, .75)',
          float: 'right',
          padding: '0 10px',
          maxWidth: 'calc(100% - 100px)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontWeight: 400,
        },
      },
      '& .esri-attribution__powered-by': {
        background: 'rgba(255, 255, 255, .75)',
        padding: '0 10px',
      },
    },
  },
  '& .esri-view-root': {
    width: '100%',
    height: '100%',
  },
  '& .esri-search--show-suggestions .esri-search__suggestions-menu': {
    maxHeight: '180px',
    zIndex: '10001 !important',
  },
  '& .esri-snapping-controls__layer-list': {
    '.esri-snapping-controls__layer-list__item': {
      '--tw-shadow': '0 0 #0000', // Hides seperators between layers
      '&:nth-of-type(2), &:nth-of-type(3)': {
        display: 'none', // All Sketch related Layers
      },
      '&:first-of-type': { // "Nearby Records" layer
        borderBottom: '1.25px solid var(--calcite-ui-border-3)',
        marginBottom: '8px',
        paddingBottom: '8px',
      },
    },
  },
  // Force crosshair cursor on all canvas elements when the lasso draw tool is active.
  // ArcGIS sets its own cursor on canvases in freehand mode, so !important is required.
  [`&.${LASSO_DRAW_ACTIVE_CLASS} canvas`]: {
    cursor: 'crosshair !important',
  },
});

interface Props {
  extents?: Extents,
  plugins?: MapPlugin[],
  parentView: string,
  onMapLoaded?: () => void,
  noKeys?: boolean,
  takeScreenshot?: (screenshot: Promise<{ dataUrl: string }>) => void,
  screenshotRequestedAt?: number,
}

export const ArcGisMapView: React.FC<Props> = ({
  extents,
  plugins = [],
  parentView,
  onMapLoaded,
  takeScreenshot,
  screenshotRequestedAt,
  noKeys = false,
}: Props) => {
  const mapViewRef = useRef<MapView | null>(null);
  const mapRef = useRef(null);
  const app = useApp();

  // Initialize arcGIS sdk
  if (esriConfig.apiKey === undefined && !noKeys) {
    console.log('Initializing the ArcGIS SDK');
    esriConfig.apiKey = app.globals.config.esri_api_key;
  }

  const pluginComponents = plugins.filter((p) => !!p.component).map((p) => p.component);
  const { layerSettings } = noKeys ? { layerSettings: { activeBasemap: { id: undefined } } } : app;
  const { activeBasemap } = noKeys ? { activeBasemap: { id: undefined } } : layerSettings;
  const [mapView, setMapView] = useState<MapView|undefined>(undefined);

  useEffect(() => {
    if (takeScreenshot && screenshotRequestedAt && mapView) {
      takeScreenshot(mapView.takeScreenshot());
    }
  }, [screenshotRequestedAt, takeScreenshot, mapView]);

  useEffect(() => {
    const { latitude, longitude, zoom } = (
      extents ?? defaultExtents
    );
    const eventHandler = onMapLoaded ?? mapLoadedEvent;

    if (mapRef.current) {
      const arcgisFactory = arcGisFactory();

      const map = arcgisFactory.newMap({
        basemap: layerSettings?.activeBasemap?.esriMapTypeId || SAFE_DEFAULT_BASEMAP.esriMapTypeId,
      });

      // Multiple DeckLayers causes conflicts, so ArcGisMapView will create
      // the single FULCRUM_DECK_LAYER_ID DeckLayer. MapPlugins wishing to
      // interact with the DeckLayer should `mapView.map.findLayerById(FULCRUM_DECK_LAYER_ID)`
      const deckLayer = arcgisFactory.newDeckGLLayer({
        'deck.layers': [],
        'deck.getCursor': ({ isDragging }) => (isDragging ? 'grabbing' : 'inherit'),
        'deck.getTooltip': ({ object }) => (object?.tooltip && {
          html: `<p>${object.tooltip}</p>`,
          style: {
            backgroundColor: '#fcfcfc',
            color: '#060606',
            fontSize: '1em',
            lineHeight: '0.3em',
            padding: '0 10px',
          },
        }),
      });
      deckLayer.id = FULCRUM_DECK_LAYER_ID;
      map.add(deckLayer);

      const view = arcgisFactory.newMapView({
        map,
        center: { type: 'point', latitude, longitude },
        zoom,
        container: mapRef.current,
        constraints: {
          lods: TileInfo.create({
            spatialReference: SpatialReference.WGS84,
          }).lods,
          minZoom: MIN_ZOOM,
          rotationEnabled: false,
        },
        background: {
          color: [255, 252, 244, 0.5],
        },
        popup: {
          defaultPopupTemplateEnabled: true,
          dockEnabled: true,
          dockOptions: {
            position: MapPlacement.TopRight,
            breakpoint: false,
          },
        },
      });

      plugins.forEach((plugin) => {
        plugin.initMap?.(view);
      });

      view.when(() => {
        plugins.forEach((plugin) => {
          plugin.extendMap?.(view);
        });
      });

      mapViewRef.current = view;
      setMapView(view);
    }

    eventHandler(
      'ESRI',
      activeBasemap.id,
      noKeys ? [] : app.layerSettings.activeOverlays,
      parentView,
    );

    return () => {
      if (mapViewRef.current) {
        if (typeof mapViewRef.current.destroy === 'function') {
          mapViewRef.current.map.removeAll();
          mapViewRef.current.destroy();
        }

        mapViewRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (mapView) {
      plugins.filter((p) => p.onChange)
        .forEach((plugin) => {
          plugin.onChange?.(mapView);
        });
    }
  });

  return (
    <StyledBox className={activeBasemap?.type || ''} data-testid="arcgis-map-view" ref={mapRef}>
      {pluginComponents
        .filter((comp): comp is MapComponent => !!comp)
        .map((comp) => <Box key={comp.key}>{ comp.element(mapView) }</Box>)}
    </StyledBox>
  );
};

export default ArcGisMapView;
